// web/src/routes/Pages.tsx — the routed IA pages behind the app shell (M4).
//
// Audit renders the live, tamper-evident timeline. Requests, My Access, and
// Responsibilities are now real, token-only surfaces bound to the records read-path
// (api.listRecords / api.listResponsibilities) and the quorum status endpoints.
// Settings shows the real identity + keys surface; org policies are honestly marked
// as not configurable in this build (no faux "coming soon" inline rows). METADATA ONLY throughout —
// these surfaces never read a shard, key, or plaintext (R11).
import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRightLeft,
  Check,
  Fingerprint,
  Inbox,
  KeyRound,
  Minus,
  Settings as SettingsIcon,
  ShieldCheck,
  UserRound,
  Zap,
} from 'lucide-react';
import type {
  AuditEntry,
  PendingProof,
  RecordSummary,
  ResponsibilityView,
} from '../../../shared/types';
import { roleCapabilities } from '../lib/trust';
import { useConsoleData } from './Console';
import { AuditFeed } from '../components/AuditFeed';
import { api } from '../api';
import { signingKeyManager } from '../crypto';
import { useAsync } from '../hooks';
import { Icon } from '../components/ui/Icon';
import { PageHeader } from '../components/ui/PageHeader';
import { Badge, EmptyState, FeedbackMessage } from '../components/ui';
import { BreakGlassDialog } from '../components/records/BreakGlassDialog';
import { useConveneUnseal } from '../components/records/useConveneUnseal';
import { ShareCeremonyPanel } from '../components/ceremony/ShareCeremonyPanel';
import {
  TransferOwnershipDialog,
  candidatesFrom,
} from '../components/records/TransferOwnershipDialog';
import {
  ago,
  freshProofs,
  isLiveCeremony,
  possessionBadge,
  quorumHealth,
  recordName,
  unsealLabel,
} from '../lib/records';

function PageFrame({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <div className="bgPage">{children}</div>;
}

function ListSkeleton({ label }: { label: string }): React.JSX.Element {
  return (
    <ul className="bgSkeletonList" aria-busy="true" aria-label={label}>
      {[0, 1, 2].map((i) => (
        <li key={i} className="bgSkeletonRow" />
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Shard-proof inbox (M2.7) — outstanding possession-proof challenges across every
// record the caller holds a shard in. The system periodically asks each holder to
// prove they still control their shard's key. For each item we sign the PUBLIC
// nonce CLIENT-SIDE with the holder's ECDSA signing key and POST the signature
// (api.answerPossession). The signature proves key control and reveals nothing —
// no shard, key, or plaintext ever leaves the device (R11).
// ---------------------------------------------------------------------------

/** Per-item answer state so each row shows its own loading / done / error. */
type ProofState = 'idle' | 'signing' | 'done' | 'error';

export function ProofsPage(): React.JSX.Element {
  const proofsState = useAsync<PendingProof[]>(api.pendingProofs);
  const proofs = proofsState.data ?? [];

  const [rowState, setRowState] = useState<Record<string, ProofState>>({});
  const [rowError, setRowError] = useState<Record<string, string>>({});

  // Resolve the holder's local ECDSA signing key id, sign the nonce on-device, and
  // submit. The private key is non-extractable and never leaves the browser; only
  // the signature over the public nonce is sent.
  const prove = React.useCallback(
    async (proof: PendingProof) => {
      setRowState((s) => ({ ...s, [proof.proofId]: 'signing' }));
      setRowError((s) => ({ ...s, [proof.proofId]: '' }));
      try {
        const keys = await signingKeyManager.listSigningKeys();
        if (keys.length === 0) {
          throw new Error(
            'No signing key on this device. Open this on the device you enrolled with.',
          );
        }
        const signature = await signingKeyManager.signNonce(keys[0].keyId, proof.nonce);
        await api.answerPossession(proof.proofId, signature);
        setRowState((s) => ({ ...s, [proof.proofId]: 'done' }));
      } catch (e) {
        setRowState((s) => ({ ...s, [proof.proofId]: 'error' }));
        setRowError((s) => ({
          ...s,
          [proof.proofId]: e instanceof Error ? e.message : String(e),
        }));
      }
    },
    [],
  );

  const open = proofs.filter((p) => rowState[p.proofId] !== 'done');

  return (
    <PageFrame>
      <PageHeader
        kicker="SHARD PROOFS"
        title="Prove you still hold your shard"
        subtitle="From time to time the system asks each holder to confirm they still control their shard. Answering signs a challenge on this device — it proves key control and reveals nothing."
      />
      {proofsState.loading && proofs.length === 0 ? (
        <ListSkeleton label="Loading proof challenges" />
      ) : open.length === 0 ? (
        <div className="bgEmptyWrap">
          <EmptyState
            title="Nothing to prove right now"
            subtitle="When a possession-proof is due for a shard you hold, it appears here."
          />
          <Link to="/my-access" className="btn btn-sm">
            <Icon icon={KeyRound} size="xs" /> See what you hold
          </Link>
        </div>
      ) : (
        <ul className="bgRequestList">
          {open.map((p) => {
            const st = rowState[p.proofId] ?? 'idle';
            const busy = st === 'signing';
            return (
              <li key={p.proofId} className="bgRequestRow">
                <span className="bgRequestSpark" aria-hidden="true">
                  <Icon icon={Fingerprint} size="sm" tone="accent" />
                </span>
                <span className="bgRequestBody">
                  <span className="bgRequestName">{p.recordName}</span>
                  <span className="bgRequestMeta">
                    Proof requested {ago(p.issuedAt)} · signing happens on this device
                  </span>
                  {st === 'error' && rowError[p.proofId] && (
                    <FeedbackMessage ok={false} msg={rowError[p.proofId]} />
                  )}
                </span>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => void prove(p)}
                  disabled={busy}
                >
                  {busy ? (
                    <span className="spinner" aria-hidden="true" />
                  ) : (
                    <Icon icon={Fingerprint} size="xs" />
                  )}
                  Prove I still hold it
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {proofsState.error && <FeedbackMessage ok={false} msg={proofsState.error} />}
    </PageFrame>
  );
}

// ---------------------------------------------------------------------------
// My Access (M4.3) — records you hold a shard in, plus any active ceremonies.
// ---------------------------------------------------------------------------

export function MyAccessPage(): React.JSX.Element {
  const navigate = useNavigate();
  const recordsState = useAsync<RecordSummary[]>(api.listRecords);
  const held = useMemo(
    () => (recordsState.data ?? []).filter((r) => r.isHolder),
    [recordsState.data],
  );

  return (
    <PageFrame>
      <PageHeader
        kicker="MY SHARDS"
        title="What you hold in custody"
        subtitle="Records you hold a shard in. There's nothing to do here day to day — when a possession-proof is due it shows up under Shard proofs, and unseal ceremonies you're asked to help with open from a join code."
      />
      {recordsState.loading && held.length === 0 ? (
        <ListSkeleton label="Loading what you hold" />
      ) : held.length === 0 ? (
        <div className="bgEmptyWrap">
          <EmptyState
            title="You're not holding any shards"
            subtitle="When someone enrolls you on a secret, your shard appears here."
          />
        </div>
      ) : (
        <ul className="bgAccessList">
          {held.map((r) => {
            const h = quorumHealth(r);
            return (
              <li
                key={r.id}
                className="bgAccessRow"
                role="link"
                tabIndex={0}
                onClick={() => navigate(`/records/${r.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/records/${r.id}`);
                  }
                }}
              >
                <span className="bgAccessKey" aria-hidden="true">
                  <Icon icon={KeyRound} size="sm" tone="accent" />
                </span>
                <span className="bgAccessBody">
                  <span className="bgAccessName">{recordName(r)}</span>
                  <span className="bgAccessMeta">
                    {r.thresholdK} of {r.totalN} to unseal{r.isOwner ? ' · you own this' : ''}
                  </span>
                </span>
                <span title={h.tooltip}>
                  <Badge tone={h.tone} label={h.label} />
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </PageFrame>
  );
}

// ---------------------------------------------------------------------------
// Audit (M4 / R11) — per-record and OWNER-ONLY. Audit is no longer a global feed
// (that surface is admin-only / gone). The page lists the records you OWN; picking
// one loads its audit slice via api.recordAudit(id), gated server-side to the
// owner. Verify the hash-chain on the selected record's slice.
// ---------------------------------------------------------------------------

export function AuditPage(): React.JSX.Element {
  const { onVerify } = useConsoleData();
  const recordsState = useAsync<RecordSummary[]>(api.listRecords);
  const owned = useMemo(
    () => (recordsState.data ?? []).filter((r) => r.isOwner),
    [recordsState.data],
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Default to the first owned record once the list resolves.
  const activeId = selectedId ?? owned[0]?.id ?? null;

  const auditState = useAsync<AuditEntry[]>(
    React.useCallback(
      () => (activeId ? api.recordAudit(activeId) : Promise.resolve([])),
      [activeId],
    ),
  );
  const entries = auditState.data ?? [];

  return (
    <PageFrame>
      <PageHeader
        kicker="AUDIT LOG"
        title="Every break, on the record"
        subtitle="A tamper-evident timeline for the records you own — who did what, and when. Pick a record to read its audit and verify the hash-chain."
      />
      {recordsState.loading && owned.length === 0 ? (
        <ListSkeleton label="Loading the records you own" />
      ) : owned.length === 0 ? (
        <div className="bgEmptyWrap">
          <EmptyState
            title="No records to audit"
            subtitle="Audit is per-record and owner-only. When you own a record, its audit trail appears here."
          />
        </div>
      ) : (
        <>
          <div className="bgKvList" style={{ marginBottom: '1rem' }}>
            <label className="field-label" htmlFor="audit-record">
              Record
            </label>
            <select
              id="audit-record"
              className="input"
              value={activeId ?? ''}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              {owned.map((r) => (
                <option key={r.id} value={r.id}>
                  {recordName(r)}
                </option>
              ))}
            </select>
          </div>
          {auditState.error ? (
            <FeedbackMessage ok={false} msg={auditState.error} />
          ) : (
            <AuditFeed entries={entries} onVerify={onVerify} />
          )}
        </>
      )}
    </PageFrame>
  );
}

// ---------------------------------------------------------------------------
// Responsibilities (M4.7 / R15) — records you own: roster, last possession proof,
// ceremony history, and the metadata-only transfer-ownership action.
// ---------------------------------------------------------------------------

export function ResponsibilitiesPage(): React.JSX.Element {
  const { principal } = useConsoleData();
  const navigate = useNavigate();
  const state = useAsync<ResponsibilityView[]>(api.listResponsibilities);
  const views = state.data ?? [];
  const convene = useConveneUnseal(principal);

  const [breakTarget, setBreakTarget] = useState<ResponsibilityView | null>(null);
  const [transferTarget, setTransferTarget] = useState<ResponsibilityView | null>(null);
  const [transferBusy, setTransferBusy] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);

  async function doTransfer(toOwnerId: string): Promise<void> {
    if (!transferTarget) return;
    setTransferBusy(true);
    setTransferError(null);
    try {
      await api.transferOwnership(transferTarget.record.id, toOwnerId);
      setTransferTarget(null);
      setTransferBusy(false);
      await state.refetch();
    } catch (e) {
      setTransferError(e instanceof Error ? e.message : String(e));
      setTransferBusy(false);
    }
  }

  return (
    <PageFrame>
      <PageHeader
        kicker="CUSTODY"
        title="Records in your charge"
        subtitle="Records you own — yours to report on and keep healthy. Possession-proof status, ceremony history, and the roster. Ownership is accountability, not access."
      />
      {state.loading && views.length === 0 ? (
        <ListSkeleton label="Loading the records you own" />
      ) : views.length === 0 ? (
        <div className="bgEmptyWrap">
          <EmptyState
            title="You're not responsible for any records"
            subtitle="Records you own will appear here."
          />
        </div>
      ) : (
        <div className="bgRespList">
          {views.map((v) => {
            const liveMembers = v.members.filter((m) => !m.shardRevoked);
            const health = quorumHealth(
              {
                status: v.record.status,
                thresholdK: v.record.thresholdK,
                totalN: v.record.totalN,
                memberCount: liveMembers.length,
                provenCount: freshProofs(v.members),
              },
              v.ceremonies.some(isLiveCeremony),
            );
            return (
              <section key={v.record.id} className="bgSection bgRespCard">
                <header className="bgSectionHead">
                  <div>
                    <div className="kicker">
                      {v.record.thresholdK} of {v.record.totalN} · {freshProofs(v.members)} of{' '}
                      {liveMembers.length} proofs fresh
                    </div>
                    <h2 className="bgSectionTitle">
                      <Link to={`/records/${v.record.id}`} className="bgRespLink">
                        {recordName(v.record)}
                      </Link>
                    </h2>
                  </div>
                  <span title={health.tooltip}>
                    <Badge tone={health.tone} label={health.label} />
                  </span>
                </header>

                <div className="bgSectionBody">
                  <ul className="bgRespRoster">
                    {v.members.map((m) => {
                      const pb = possessionBadge(m.possessionStatus);
                      return (
                        <li key={m.memberId} className="bgRespMember">
                          <span className="bgRespEmail">{m.email}</span>
                          <span className="bgFingerprint">
                            {m.fingerprint ? m.fingerprint : 'no key'}
                          </span>
                          <span className="bgSubtle">{ago(m.lastProofAt)}</span>
                          <Badge tone={pb.tone} label={pb.label} />
                        </li>
                      );
                    })}
                  </ul>

                  <div className="bgRespCeremonies">
                    {v.ceremonies.length === 0 ? (
                      <span className="bgSubtle">No unseal ceremonies yet.</span>
                    ) : (
                      v.ceremonies.slice(0, 4).map((c) => (
                        <span key={c.id} className="bgRespCeremony">
                          <Badge
                            tone={c.status === 'unlocked' ? 'success' : isLiveCeremony(c) ? 'info' : 'neutral'}
                            label={unsealLabel(c.status)}
                          />
                          <span className="bgSubtle">{ago(c.createdAt)}</span>
                        </span>
                      ))
                    )}
                  </div>

                  <div className="bgRespActions">
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => setBreakTarget(v)}
                      disabled={v.record.status !== 'sealed'}
                    >
                      <Icon icon={Zap} size="xs" /> Convene unseal
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => setTransferTarget(v)}
                    >
                      <Icon icon={ArrowRightLeft} size="xs" /> Transfer ownership
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => navigate(`/records/${v.record.id}`)}
                    >
                      Open detail
                    </button>
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      )}

      {breakTarget && !convene.share && (
        <BreakGlassDialog
          target={{
            id: breakTarget.record.id,
            description: breakTarget.record.description,
            thresholdK: breakTarget.record.thresholdK,
            totalN: breakTarget.record.totalN,
            holders: breakTarget.members
              .filter((m) => !m.shardRevoked)
              .map((m) => ({ memberId: m.memberId, label: m.email, fingerprint: m.fingerprint ?? undefined })),
          }}
          timeoutSeconds={breakTarget.record.timeoutSeconds}
          busy={convene.busy}
          error={convene.error}
          onConfirm={(reason) =>
            convene.convene(
              {
                id: breakTarget.record.id,
                description: breakTarget.record.description,
                thresholdK: breakTarget.record.thresholdK,
                totalN: breakTarget.record.totalN,
              },
              reason,
            )
          }
          onCancel={() => setBreakTarget(null)}
        />
      )}

      {convene.share && (
        <ShareCeremonyPanel
          share={convene.share}
          onEnter={convene.enter}
          onClose={() => {
            convene.dismissShare();
            setBreakTarget(null);
          }}
        />
      )}

      {transferTarget && (
        <TransferOwnershipDialog
          recordName={recordName(transferTarget.record)}
          candidates={candidatesFrom(transferTarget.members, transferTarget.record.ownerId)}
          busy={transferBusy}
          error={transferError}
          onConfirm={doTransfer}
          onCancel={() => setTransferTarget(null)}
        />
      )}
    </PageFrame>
  );
}

// ---------------------------------------------------------------------------
// Settings — the identity & session surface (M5.1 / R13). Who you are, your role,
// the keys/devices registered to you with their safety-number fingerprints, and a
// least-privilege "what you can / can't do" list. Identity comes from the resolved
// principal (GET /me, the act-as source of truth); the keys are derived from the
// records you're a member of (RecordMemberView fingerprints) matched by email —
// METADATA ONLY, no private key or shard is ever read here (R11). Policies remain
// a named stub for their later milestone.
// ---------------------------------------------------------------------------

/** A device key the caller has on file, derived from record rosters (metadata only). */
interface MyKey {
  pubkeyId: string;
  fingerprint: string | null;
}

export function SettingsPage(): React.JSX.Element {
  const { principal } = useConsoleData();
  const caps = roleCapabilities(principal.role);

  // Derive the caller's registered keys from the rosters of records they're in.
  // One detail fetch per record; best-effort and de-duplicated by pubkeyId.
  const keysState = useAsync<MyKey[]>(
    React.useCallback(async () => {
      const records = await api.listRecords();
      const byId = new Map<string, MyKey>();
      for (const r of records) {
        try {
          const d = await api.getRecord(r.id);
          for (const m of d.members) {
            if (m.email === principal.email && !byId.has(m.pubkeyId)) {
              byId.set(m.pubkeyId, { pubkeyId: m.pubkeyId, fingerprint: m.fingerprint });
            }
          }
        } catch {
          /* skip records that fail to load — the key list stays best-effort */
        }
      }
      return [...byId.values()];
    }, [principal.email]),
  );
  const keys = keysState.data ?? [];

  return (
    <PageFrame>
      <PageHeader
        kicker="SETTINGS"
        title="Identity & session"
        subtitle="Who you are, the keys registered to you, and what your role lets you do. Your identity is enforced by the server; your private keys never leave your devices."
      />

      <section className="bgSection">
        <header className="bgSectionHead">
          <div>
            <div className="kicker">You</div>
            <h2 className="bgSectionTitle">Identity</h2>
          </div>
          <Badge tone="info" label={`role: ${principal.role}`} />
        </header>
        <div className="bgSectionBody">
          <div className="bgIdentityCard">
            <span className="bgIdentityAvatar" aria-hidden="true">
              <Icon icon={UserRound} size="md" />
            </span>
            <div className="bgIdentityWho">
              <span className="bgIdentityName">{principal.name}</span>
              <span className="bgIdentityEmail">{principal.email}</span>
              <span className="bgSubtle">Acting as this identity in this session.</span>
            </div>
          </div>
        </div>
      </section>

      <div className="bgDetailGrid">
        <section className="bgSection">
          <header className="bgSectionHead">
            <div>
              <div className="kicker">Keys &amp; devices</div>
              <h2 className="bgSectionTitle">Registered keys</h2>
            </div>
          </header>
          <div className="bgSectionBody">
            {keysState.loading && keys.length === 0 ? (
              <ListSkeleton label="Loading your keys" />
            ) : keys.length === 0 ? (
              <EmptyState
                title="No keys on file yet"
                subtitle="When you join a record, the public key generated on that device appears here with its safety number."
              />
            ) : (
              <ul className="bgKeyList">
                {keys.map((k) => (
                  <li key={k.pubkeyId} className="bgKeyRow">
                    <span className="bgKeyIcon" aria-hidden="true">
                      <Icon icon={KeyRound} size="sm" />
                    </span>
                    <span className="bgKeyWho">
                      <span className="bgKeyKind">Device key</span>
                      <span className="bgKeyFp">
                        {k.fingerprint ? `safety number ${k.fingerprint}` : `key ${k.pubkeyId}`}
                      </span>
                    </span>
                    <Badge tone="success" label="Active" />
                  </li>
                ))}
              </ul>
            )}
            <p className="bgSubtle" style={{ marginTop: '0.6rem' }}>
              Your private key is created on each device and can&apos;t be copied off it. The server
              only ever holds the public key shown here.
            </p>
          </div>
        </section>

        <section className="bgSection">
          <header className="bgSectionHead">
            <div>
              <div className="kicker">Least privilege</div>
              <h2 className="bgSectionTitle">What you can &amp; can&apos;t do</h2>
            </div>
          </header>
          <div className="bgSectionBody">
            <ul className="bgCapList">
              {caps.map((c, i) => (
                <li key={i} className={`bgCapRow ${c.allowed ? '' : 'bgCapRowDeny'}`}>
                  <span className={c.allowed ? 'bgCapAllow' : 'bgCapDeny'} aria-hidden="true">
                    <Icon icon={c.allowed ? Check : Minus} size="sm" />
                  </span>
                  <span>
                    <span className="sr-only">{c.allowed ? 'You can: ' : 'You can’t: '}</span>
                    {c.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>

      <section className="bgSection">
        <header className="bgSectionHead">
          <div>
            <div className="kicker">Org &amp; defaults</div>
            <h2 className="bgSectionTitle">Policies</h2>
          </div>
          <Badge tone="neutral" label="Not configurable yet" />
        </header>
        <div className="bgSectionBody">
          <EmptyState
            title="No org policies to manage here yet"
            subtitle="Org-wide members, roles, default quorum, windows, and possession-proof cadence aren't editable in this build. Quorum and the time-box window are set per secret in the new-secret wizard."
          />
        </div>
        <div className="bgSectionBody">
          <div className="bgTrustBar">
            <Icon icon={ShieldCheck} size="sm" tone="accent" />
            The audit trail records that things happened — never the secrets themselves.
          </div>
        </div>
      </section>
    </PageFrame>
  );
}

export function NotFoundPage(): React.JSX.Element {
  return (
    <PageFrame>
      <PageHeader
        kicker="NOT FOUND"
        title="No panel here"
        subtitle="That route does not exist. Use the left navigation to find your way back."
      />
      <div className="bgEmptyWrap">
        <EmptyState title="Nothing here" subtitle="Check the address or head back to your records." />
        <Link to="/" className="btn btn-primary btn-sm">
          <Icon icon={Inbox} size="xs" /> Back to records
        </Link>
      </div>
    </PageFrame>
  );
}

export const PAGE_ICONS = { Inbox, KeyRound, SettingsIcon } as const;
